/**
 * AppICEMAP — PhotoGallery Component
 * Horizontal swipeable media strip with fullscreen lightbox.
 *
 * Slides are the report photos followed by a map of where it happened, so a
 * report without photos still shows something useful instead of an empty frame.
 *
 * Tag: <photo-gallery>
 *
 * Properties:
 *   .photos     — array of photo objects with .image.url
 *   .location   — { lat, lng, category } for the map slide (null to omit it)
 *   .startIndex — starting index (default: 0)
 *
 * Dispatches:
 *   photo-gallery:close → { }
 */

import L from "leaflet";
import { TILE_URL_DARK, TILE_ATTRIBUTION } from "../../utils/basemap.js";
import { createReportMarker } from "../map/ReportMarker.js";

export class PhotoGallery extends HTMLElement {
	constructor() {
		super();
		this._photos = [];
		/** @type {{lat:number,lng:number,category?:object}|null} */
		this._location = null;
		/** @type {L.Map|null} lazily created map for the location slide */
		this._miniMap = null;
		this._currentIndex = 0;
		this._isFullscreen = false;
	}

	connectedCallback() {
		this._render();
		this._bindEvents();
	}

	disconnectedCallback() {
		this._destroyMiniMap();
	}

	/* ── Public API ────────────────────────────────────────────────────────── */

	get photos() {
		return this._photos;
	}
	set photos(val) {
		this._photos = Array.isArray(val) ? val : [];
		this._renderContent();
	}

	get location() {
		return this._location;
	}
	set location(val) {
		this._location =
			val && Number.isFinite(val.lat) && Number.isFinite(val.lng) ? val : null;
		this._renderContent();
	}

	/** Total slides: every photo plus the map (when there is a location) */
	get _slideCount() {
		return this._photos.length + (this._location ? 1 : 0);
	}

	/** Index of the map slide, or -1 when there is no location */
	get _mapIndex() {
		return this._location ? this._photos.length : -1;
	}

	get startIndex() {
		return this._currentIndex;
	}
	set startIndex(val) {
		this._currentIndex = Math.max(0, Math.min(val || 0, this._slideCount - 1));
		this._scrollToCurrent();
	}

	/* ── Render ────────────────────────────────────────────────────────────── */

	_render() {
		this.innerHTML = `
      <div class="photo-gallery">
        <div class="photo-gallery__viewport"></div>
        <div class="photo-gallery__dots"></div>
        <div class="photo-gallery__nav">
          <button class="photo-gallery__prev" aria-label="Foto anterior" disabled>
            <i class="ph ph-caret-left" aria-hidden="true"></i>
          </button>
          <span class="photo-gallery__counter"></span>
          <button class="photo-gallery__next" aria-label="Foto siguiente" disabled>
            <i class="ph ph-caret-right" aria-hidden="true"></i>
          </button>
        </div>
        <button class="photo-gallery__fullscreen-btn" aria-label="Pantalla completa">
          <i class="ph ph-arrows-out" aria-hidden="true"></i>
        </button>
      </div>
    `;

		this._viewport = this.querySelector(".photo-gallery__viewport");
		this._dotsEl = this.querySelector(".photo-gallery__dots");
		this._counterEl = this.querySelector(".photo-gallery__counter");
		this._prevBtn = this.querySelector(".photo-gallery__prev");
		this._nextBtn = this.querySelector(".photo-gallery__next");
		this._fullscreenBtn = this.querySelector(".photo-gallery__fullscreen-btn");
		this._galleryEl = this.querySelector(".photo-gallery");

		this._renderContent();
	}

	_renderContent() {
		if (!this._viewport) return;

		// Nothing to show at all — no photos and no coordinates
		if (this._slideCount === 0) {
			this.hidden = true;
			this._destroyMiniMap();
			return;
		}

		this.hidden = false;
		this._currentIndex = Math.min(this._currentIndex, this._slideCount - 1);

		// A rebuild throws away the old map node, so drop the Leaflet instance
		this._destroyMiniMap();

		let html = this._photos
			.map((p) => {
				const url = p.image?.url || p.image || p.url || "";
				return `<img src="${url}" alt="Foto del reporte" loading="lazy">`;
			})
			.join("");

		if (this._location) {
			html += `
        <div class="photo-gallery__map-slide">
          <div class="photo-gallery__map"></div>
          <span class="photo-gallery__map-label">
            <i class="ph ph-map-pin" aria-hidden="true"></i> Ubicación del reporte
          </span>
        </div>
      `;
		}

		this._viewport.innerHTML = html;

		if (this._location) this._mountMiniMap();

		// Dots
		if (this._slideCount > 1) {
			this._dotsEl.innerHTML = Array.from({ length: this._slideCount })
				.map((_, i) => {
					const label =
						i === this._mapIndex ? "Ir al mapa" : `Ir a foto ${i + 1}`;
					return `<button class="photo-gallery__dot${i === this._currentIndex ? " photo-gallery__dot--active" : ""}"
                    data-index="${i}" aria-label="${label}"></button>`;
				})
				.join("");
		} else {
			this._dotsEl.innerHTML = "";
		}

		this._updateNav();
	}

	/* ── Map slide ─────────────────────────────────────────────────────────── */

	/**
	 * Create a small, non-interactive map centred on the report. It is display
	 * only: panning and zooming stay with the main map so a swipe through the
	 * carousel never gets captured by it.
	 */
	_mountMiniMap() {
		const host = this._viewport?.querySelector(".photo-gallery__map");
		if (!host || !this._location) return;

		const { lat, lng, category } = this._location;

		this._miniMap = L.map(host, {
			center: [lat, lng],
			zoom: 16,
			zoomControl: false,
			attributionControl: true,
			dragging: false,
			scrollWheelZoom: false,
			doubleClickZoom: false,
			touchZoom: false,
			boxZoom: false,
			keyboard: false,
		});

		L.tileLayer(TILE_URL_DARK, {
			attribution: TILE_ATTRIBUTION,
			maxZoom: 19,
		}).addTo(this._miniMap);

		L.marker([lat, lng], {
			icon: createReportMarker({ id: "preview", category }),
			interactive: false,
			keyboard: false,
		}).addTo(this._miniMap);

		// The slide is laid out after this runs; Leaflet needs a nudge to measure
		requestAnimationFrame(() => this._miniMap?.invalidateSize());
	}

	_destroyMiniMap() {
		if (this._miniMap) {
			this._miniMap.remove();
			this._miniMap = null;
		}
	}

	/* ── Events ────────────────────────────────────────────────────────────── */

	_bindEvents() {
		// Viewport scroll → update index
		if (this._viewport) {
			this._viewport.addEventListener("scroll", () => {
				const index = Math.round(
					this._viewport.scrollLeft / this._viewport.clientWidth,
				);
				if (
					index !== this._currentIndex &&
					index >= 0 &&
					index < this._slideCount
				) {
					this._currentIndex = index;
					this._updateNav();
				}
			});
		}

		// Navigation buttons
		if (this._prevBtn) {
			this._prevBtn.addEventListener("click", () => this.navigate(-1));
		}
		if (this._nextBtn) {
			this._nextBtn.addEventListener("click", () => this.navigate(1));
		}

		// Dot clicks
		if (this._dotsEl) {
			this._dotsEl.addEventListener("click", (e) => {
				const dot = e.target.closest(".photo-gallery__dot");
				if (!dot) return;
				const index = parseInt(dot.dataset.index, 10);
				if (!isNaN(index)) {
					this._currentIndex = index;
					this._scrollToCurrent();
				}
			});
		}

		// Tocar la foto la amplía: es el gesto que la gente espera, y el botón
		// de pantalla completa es pequeño en un móvil.
		this._viewport?.addEventListener("click", (e) => {
			if (e.target.tagName === "IMG" && !this._isFullscreen) {
				this._toggleFullscreen();
			}
		});

		// Fullscreen toggle
		if (this._fullscreenBtn) {
			this._fullscreenBtn.addEventListener("click", () =>
				this._toggleFullscreen(),
			);
		}

		// Keyboard
		this.addEventListener("keydown", (e) => {
			if (e.key === "ArrowLeft") this.navigate(-1);
			if (e.key === "ArrowRight") this.navigate(1);
			if (e.key === "Escape" && this._isFullscreen) this._toggleFullscreen();
		});

		// Touch swipe
		let touchStartX = 0;
		if (this._viewport) {
			this._viewport.addEventListener(
				"touchstart",
				(e) => {
					touchStartX = e.touches[0].clientX;
				},
				{ passive: true },
			);
		}
	}

	/* ── Navigation ────────────────────────────────────────────────────────── */

	navigate(delta) {
		const newIndex = this._currentIndex + delta;
		if (newIndex < 0 || newIndex >= this._slideCount) return;
		this._currentIndex = newIndex;
		this._scrollToCurrent();
	}

	_scrollToCurrent() {
		if (!this._viewport) return;
		const scrollTo = this._currentIndex * this._viewport.clientWidth;
		this._viewport.scrollTo({ left: scrollTo, behavior: "smooth" });
		this._updateNav();
	}

	_updateNav() {
		const count = this._slideCount;

		// Counter
		if (this._counterEl) {
			this._counterEl.textContent =
				count > 1 ? `${this._currentIndex + 1} / ${count}` : "";
		}

		// Prev/next buttons
		if (this._prevBtn) this._prevBtn.disabled = this._currentIndex === 0;
		if (this._nextBtn) this._nextBtn.disabled = this._currentIndex >= count - 1;

		// Dots
		const dots = this._dotsEl?.querySelectorAll(".photo-gallery__dot");
		if (dots) {
			dots.forEach((d, i) => {
				d.classList.toggle(
					"photo-gallery__dot--active",
					i === this._currentIndex,
				);
			});
		}
	}

	/* ── Fullscreen ────────────────────────────────────────────────────────── */

	_toggleFullscreen() {
		this._isFullscreen = !this._isFullscreen;

		if (this._galleryEl) {
			this._galleryEl.classList.toggle(
				"photo-gallery--fullscreen",
				this._isFullscreen,
			);

			if (this._isFullscreen) {
				document.body.style.overflow = "hidden";

				// Add close button in fullscreen
				if (
					!this._galleryEl.querySelector(
						".photo-gallery--fullscreen .photo-gallery__close",
					)
				) {
					const closeBtn = document.createElement("button");
					closeBtn.className = "photo-gallery__close";
					closeBtn.setAttribute("aria-label", "Cerrar pantalla completa");
					closeBtn.innerHTML = '<i class="ph ph-x" aria-hidden="true"></i>';
					closeBtn.addEventListener("click", () => this._toggleFullscreen());
					this._galleryEl.appendChild(closeBtn);
				}

				this._viewport.scrollTo({
					left: this._currentIndex * this._viewport.clientWidth,
				});
				requestAnimationFrame(() => this._miniMap?.invalidateSize());
			} else {
				document.body.style.overflow = "";
				requestAnimationFrame(() => this._miniMap?.invalidateSize());
			}
		}
	}
}

customElements.define("photo-gallery", PhotoGallery);
